// A list's "+" (make a new one): in the list's header row, or on a phone a
// round button on the Dock, in the thumb's reach (the old web's, 2026-09-28).
// With `items` it opens a menu of what to make (a document or an index).
// A host that draws the Dock (a native app) gets no Dock button for it: the
// list's header hands the same "+" as data (`add`), which the host draws.
import { Link } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { ICON_BUTTON } from "../ui/button";
import { Icon } from "../ui/icon";
import { focusMenuOnOpen, Menu, MenuButton, type MenuItem } from "../ui/menu";
import { Tooltip } from "../ui/tooltip";
import { Dock, DockButton, usePhone } from "./phone";

export function ListAddButton({
  label,
  to,
  params,
  items,
}: {
  label: string;
  /** Where "+" goes, when it is one thing to make. */
  to?: string;
  params?: Record<string, string>;
  /** What "+" offers, when it is more than one thing. */
  items?: readonly MenuItem[];
}) {
  const phone = usePhone();
  if (items) return <ListAddMenu label={label} items={items} phone={phone} />;
  if (phone)
    return (
      <Dock kind="cluster">
        <DockButton
          label={label}
          to={to}
          params={params}
          icon={<Icon kind="plus" size={20} />}
        />
      </Dock>
    );
  return (
    <Tooltip text={label}>
      <Link
        to={to as never}
        params={params as never}
        aria-label={label}
        className={ICON_BUTTON}
      >
        <Icon kind="plus" size={15} />
      </Link>
    </Tooltip>
  );
}

function ListAddMenu({
  label,
  items,
  phone,
}: {
  label: string;
  items: readonly MenuItem[];
  phone: boolean;
}) {
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  if (!phone)
    return (
      <MenuButton
        label={label}
        items={items}
        align="end"
        className={ICON_BUTTON}
      >
        <Icon kind="plus" size={15} />
      </MenuButton>
    );
  // The list stays outside the Dock: the Dock's bar is moved by `translate`,
  // which makes a fixed list inside it measure from the bar, not the screen
  // (it opened below the bottom edge, out of sight).
  return (
    <>
      <Dock kind="cluster">
        <DockButton
          ref={button}
          label={label}
          expanded={open}
          onClick={() => {
            setOpen((v) => !v);
            focusMenuOnOpen(list, false);
          }}
          icon={<Icon kind="plus" size={20} />}
        />
      </Dock>
      <Menu
        anchor={button}
        open={open}
        onClose={close}
        label={label}
        items={items}
        listRef={list}
      />
    </>
  );
}
